import api_base from '@/api/base';
import { CaseType } from '@/views/cases/data/schema';
import { Role } from '@/views/users/data/schema';
import { refreshCabinetCaseTypes } from '@/i18n';
import { PlanListParams, PaginatedPlanResponse } from '@/types/plan';
import { CabinetSubscriptionCreate, PaginatedSubscriptionResponse, Subscription, CabinetInvoice } from '@/types/subscription';
import type { CabinetUsageCurrent, MonthlyUsage, PaymentInstructions, UpcomingPayment, RenewalPayment } from '@/types/subscription';

// Case type management
export const get_all_case_types = async (): Promise<CaseType[]> => {
  const response = await api_base.get('/api/v1/case/type/list?all');
  return response.data;
};

export const get_cabinet_case_types = async (): Promise<CaseType[]> => {
  const response = await api_base.get('/api/v1/case/type/list?cabinet');
  return response.data;
};

export const add_cabinet_case_type = async (caseTypeId: number): Promise<void> => {
  await api_base.post(`/api/v1/cabinet-case-type/create`, { case_type: caseTypeId });
  // Refresh the global cache to reflect the new case type
  await refreshCabinetCaseTypes();
};

export const remove_cabinet_case_type = async (caseTypeId: number): Promise<void> => {
  await api_base.delete(`/api/v1/cabinet-case-type/remove/${caseTypeId}`);
  // Refresh the global cache to reflect the removed case type
  await refreshCabinetCaseTypes();
};

// Role management
export const get_all_roles = async (): Promise<Role[]> => {
  const response = await api_base.get('/api/v1/user/role/list?all');
  return response.data;
};

export const get_cabinet_roles = async (): Promise<Role[]> => {
  const response = await api_base.get('/api/v1/user/role/list?cabinet');
  return response.data;
};

export const add_cabinet_role = async (roleId: number): Promise<void> => {
  await api_base.post(`/api/v1/cabinet-role/create`, { role: roleId });
};

export const remove_cabinet_role = async (roleId: number): Promise<void> => {
  await api_base.delete(`/api/v1/cabinet-role/remove/${roleId}`);
};

// Plans (cabinet)
export const get_cabinet_plans = async (
  params: PlanListParams = {}
): Promise<PaginatedPlanResponse> => {
  const response = await api_base.get('/api/v1/cabinet/plans/', { params });
  return response.data;
};

// Subscriptions (cabinet)
export const list_cabinet_subscriptions = async (
  params?: { page?: number; page_size?: number }
): Promise<PaginatedSubscriptionResponse> => {
  const response = await api_base.get('/api/v1/cabinet/subscription/list', { params });
  return response.data;
};

export const create_cabinet_subscription = async (
  payload: CabinetSubscriptionCreate
): Promise<Subscription> => {
  const response = await api_base.post('/api/v1/cabinet/subscription/create', payload);
  return response.data;
};

// Platform bank details for wire-transfer payments
export const get_payment_instructions = async (): Promise<PaymentInstructions> => {
  const response = await api_base.get('/api/v1/cabinet/billing/payment-instructions/');
  return response.data;
};

export const get_upcoming_payment = async (): Promise<UpcomingPayment> => {
  const response = await api_base.get('/api/v1/cabinet/subscription/upcoming-payment');
  return response.data;
};

// Creates a pending wire payment; the period rolls once a super-admin validates it
export const renew_cabinet_subscription = async (): Promise<RenewalPayment> => {
  const response = await api_base.post('/api/v1/cabinet/subscription/renew-now');
  return response.data;
};

// Cabinet billing invoices
export const list_cabinet_invoices = async (): Promise<CabinetInvoice[]> => {
  const response = await api_base.get('/api/v1/cabinet/billing/invoices/');
  return response.data;
};

// Cabinet usage current (plan & usage)
export const get_cabinet_usage_current = async (): Promise<CabinetUsageCurrent> => {
  const response = await api_base.get('/api/v1/cabinet/usage/current');
  return response.data;
};

// Cabinet monthly usage (detailed analytics)
export const get_cabinet_usage_monthly = async (params?: {
  year?: number;
  month?: number;
}): Promise<MonthlyUsage[]> => {
  const response = await api_base.get('/api/v1/cabinet/usage/monthly', { params });
  return response.data;
};
